// src/pages/guide/AppGuideManager.jsx
import { useEffect, useState, useRef } from 'react';
import {
  MdAdd,
  MdCloudUpload,
  MdDelete,
  MdEdit,
  MdHelpOutline,
  MdArrowUpward,
  MdArrowDownward,
  MdCheckCircle,
  MdImage,
  MdAutoAwesome,
  MdClose,
  MdVisibility,
} from 'react-icons/md';
import {
  subscribeToGuideSteps,
  saveGuideStep,
  deleteGuideStep,
  uploadGuideScreenshot,
  seedDefaultGuideSteps,
} from '../../services/appGuideService';

const AppGuideManager = () => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [previewImageModal, setPreviewImageModal] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order: 1,
    imageUrl: '',
    isActive: true,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    const unsub = subscribeToGuideSteps((data) => {
      setSteps(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const openAddModal = () => {
    setCurrentStep(null);
    setFormData({
      title: '',
      description: '',
      order: steps.length + 1,
      imageUrl: '',
      isActive: true,
    });
    setSelectedFile(null);
    setPreviewUrl('');
    setUploadProgress(0);
    setErrorMsg('');
    setIsModalOpen(true);
  };

  const openEditModal = (step) => {
    setCurrentStep(step);
    setFormData({
      title: step.title || '',
      description: step.description || '',
      order: step.order || 1,
      imageUrl: step.imageUrl || '',
      isActive: step.isActive !== undefined ? step.isActive : true,
    });
    setSelectedFile(null);
    setPreviewUrl(step.imageUrl || '');
    setUploadProgress(0);
    setErrorMsg('');
    setIsModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrorMsg('Please select a valid image file (PNG, JPG, WEBP).');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setErrorMsg('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setErrorMsg('Please enter a step title.');
      return;
    }
    if (!formData.description.trim()) {
      setErrorMsg('Please enter an explanation for this step.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      let finalImageUrl = formData.imageUrl;

      // Upload file to Cloudinary if selected
      if (selectedFile) {
        finalImageUrl = await uploadGuideScreenshot(selectedFile, (progress) => {
          setUploadProgress(Math.round(progress));
        });
      }

      await saveGuideStep({
        id: currentStep?.id,
        title: formData.title,
        description: formData.description,
        order: Number(formData.order || 1),
        imageUrl: finalImageUrl || '',
        isActive: formData.isActive,
      });

      setIsModalOpen(false);
      setCurrentStep(null);
    } catch (err) {
      console.error('Error saving guide step:', err);
      setErrorMsg(err.message || 'Failed to save guide step. Please try again.');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    if (!stepToDelete) return;
    try {
      await deleteGuideStep(stepToDelete.id);
      setIsDeleteModalOpen(false);
      setStepToDelete(null);
    } catch (err) {
      alert('Failed to delete step: ' + err.message);
    }
  };

  const handleMoveOrder = async (step, direction) => {
    const currentIndex = steps.findIndex((s) => s.id === step.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= steps.length) return;

    const targetStep = steps[targetIndex];
    try {
      await saveGuideStep({ id: step.id, ...step, order: targetStep.order });
      await saveGuideStep({ id: targetStep.id, ...targetStep, order: step.order });
    } catch (err) {
      console.error('Failed to swap orders:', err);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('Seed recommended starter guide steps for SmartBrain App?')) return;
    setIsSeeding(true);
    try {
      await seedDefaultGuideSteps();
    } catch (err) {
      alert('Error seeding default steps: ' + err.message);
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 rounded-2xl p-6 text-white shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            How to Use SmartBrain
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {steps.length === 0 && (
            <button
              onClick={handleSeedDefaults}
              disabled={isSeeding}
              className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition-all border border-white/20 disabled:opacity-50"
            >
              {isSeeding ? 'Loading...' : 'Load Sample Steps'}
            </button>
          )}
          <button
            onClick={openAddModal}
            className="px-5 py-2.5 rounded-xl bg-white text-blue-700 hover:bg-blue-50 font-bold text-sm transition-all shadow-sm flex items-center gap-2"
          >
            <MdAdd className="text-lg" />
            Add Step
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Steps</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{steps.length}</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl">
            <MdHelpOutline />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Steps</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">
              {steps.filter((s) => s.isActive).length}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl">
            <MdCheckCircle />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mobile Status</p>
            <p className="text-sm font-bold text-slate-700 mt-2">
              {steps.filter((s) => s.isActive).length > 0 ? 'Live in App' : 'Needs Steps'}
            </p>
          </div>
        </div>
      </div>

      {/* Guide Steps List */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent" />
          <p className="text-slate-500 text-sm mt-3 font-medium">Loading guide steps...</p>
        </div>
      ) : steps.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center border border-dashed border-slate-300 max-w-xl mx-auto space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto text-3xl">
            <MdHelpOutline />
          </div>
          <h3 className="text-lg font-bold text-slate-800">No steps added yet</h3>
          <p className="text-slate-500 text-sm">
            Add your app screenshots and explanations so students can learn how the app works.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={handleSeedDefaults}
              disabled={isSeeding}
              className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-all shadow-sm"
            >
              {isSeeding ? 'Loading...' : 'Load Sample Steps'}
            </button>
            <button
              onClick={openAddModal}
              className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm transition-all flex items-center gap-2"
            >
              <MdAdd />
              Add First Step
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((step, idx) => (
            <div
              key={step.id}
              className={`bg-white rounded-2xl border transition-all duration-200 flex flex-col overflow-hidden shadow-sm hover:shadow-md ${
                step.isActive ? 'border-slate-200' : 'border-slate-200 opacity-60 bg-slate-50/50'
              }`}
            >
              {/* Screenshot Header Preview */}
              <div className="relative h-48 bg-slate-100 overflow-hidden border-b border-slate-100 group">
                {step.imageUrl ? (
                  <img
                    src={step.imageUrl}
                    alt={step.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-1 bg-gradient-to-br from-slate-100 to-slate-200">
                    <MdImage className="text-4xl text-slate-300" />
                    <span className="text-xs font-semibold">No Screenshot Added</span>
                  </div>
                )}

                {/* Step badge */}
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-slate-900/80 backdrop-blur-md text-white text-xs font-extrabold tracking-wider shadow">
                  STEP {step.order || idx + 1}
                </div>

                {/* Status badge */}
                <div className="absolute top-3 right-3">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold backdrop-blur-md ${
                      step.isActive ? 'bg-emerald-500/90 text-white' : 'bg-rose-500/90 text-white'
                    }`}
                  >
                    {step.isActive ? 'Active' : 'Hidden'}
                  </span>
                </div>

                {/* Quick view button */}
                {step.imageUrl && (
                  <button
                    onClick={() => setPreviewImageModal(step.imageUrl)}
                    className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-slate-700 flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                    title="View full image"
                  >
                    <MdVisibility />
                  </button>
                )}
              </div>

              {/* Step Info Content */}
              <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <h3 className="font-bold text-slate-800 text-base leading-snug line-clamp-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-4">
                    {step.description}
                  </p>
                </div>

                {/* Card Actions Footer */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                  {/* Reorder Buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleMoveOrder(step, 'up')}
                      disabled={idx === 0}
                      className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:pointer-events-none"
                      title="Move Up"
                    >
                      <MdArrowUpward className="text-sm" />
                    </button>
                    <button
                      onClick={() => handleMoveOrder(step, 'down')}
                      disabled={idx === steps.length - 1}
                      className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:pointer-events-none"
                      title="Move Down"
                    >
                      <MdArrowDownward className="text-sm" />
                    </button>
                  </div>

                  {/* Edit / Delete Buttons */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => openEditModal(step)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors flex items-center gap-1"
                    >
                      <MdEdit />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setStepToDelete(step);
                        setIsDeleteModalOpen(true);
                      }}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                      title="Delete Step"
                    >
                      <MdDelete />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Step Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl border border-slate-100 my-8 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                  {formData.order}
                </div>
                <h3 className="font-bold text-slate-900 text-lg">
                  {currentStep ? 'Edit Walkthrough Step' : 'Add New Walkthrough Step'}
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center"
              >
                <MdClose />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {errorMsg && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-semibold">
                  {errorMsg}
                </div>
              )}

              {/* Order & Status Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                    Step Order #
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                    Visibility
                  </label>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300"
                    />
                    <span className="text-sm font-semibold text-slate-700">
                      Visible in Mobile App
                    </span>
                  </label>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                  Step Headline / Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Chat with AI Tutor Zikora on any Topic"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                  Explanation / Instructions <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  placeholder="Explain clearly to students what to tap or do on this screen..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600"
                  required
                />
              </div>

              {/* Screenshot Upload & Preview */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                  Screenshot / Illustration Image
                </label>

                {previewUrl ? (
                  <div className="relative rounded-2xl border border-slate-200 overflow-hidden bg-slate-50 max-h-52 flex items-center justify-center group">
                    <img src={previewUrl} alt="Preview" className="w-full h-48 object-contain" />
                    <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3 py-1.5 rounded-lg bg-white text-slate-800 text-xs font-bold"
                      >
                        Change Image
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          setPreviewUrl('');
                          setFormData({ ...formData, imageUrl: '' });
                        }}
                        className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 hover:border-blue-500 rounded-2xl p-6 text-center cursor-pointer transition-colors bg-slate-50/50 hover:bg-blue-50/30"
                  >
                    <MdCloudUpload className="mx-auto text-3xl text-blue-500 mb-1" />
                    <p className="text-xs font-bold text-slate-700">
                      Click to upload mobile app screenshot
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">PNG, JPG or WEBP (up to 5MB)</p>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />

                {/* Direct Image URL fallback */}
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[11px] text-slate-400 whitespace-nowrap">Or Image URL:</span>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={formData.imageUrl}
                    onChange={(e) => {
                      setFormData({ ...formData, imageUrl: e.target.value });
                      if (!selectedFile) setPreviewUrl(e.target.value);
                    }}
                    className="flex-1 px-2.5 py-1 rounded-lg border border-slate-200 text-xs text-slate-600 focus:outline-none"
                  />
                </div>

                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-blue-600 font-semibold mb-1">
                      <span>Uploading to Cloudinary...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-600 font-semibold text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting ? 'Saving Step...' : currentStep ? 'Update Step' : 'Create Step'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && stepToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full space-y-4 shadow-xl border border-slate-100 text-center">
            <div className="w-12 h-12 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center mx-auto text-2xl">
              <MdDelete />
            </div>
            <h4 className="font-bold text-slate-800 text-lg">Delete Guide Step?</h4>
            <p className="text-slate-500 text-xs leading-relaxed">
              Are you sure you want to delete step <strong>&quot;{stepToDelete.title}&quot;</strong>? This cannot be undone.
            </p>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 shadow-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Screenshot Preview Modal */}
      {previewImageModal && (
        <div
          onClick={() => setPreviewImageModal(null)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md cursor-pointer"
        >
          <div className="relative max-w-2xl w-full max-h-[85vh] bg-white rounded-3xl overflow-hidden shadow-2xl p-2">
            <button
              onClick={() => setPreviewImageModal(null)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-slate-900/70 text-white flex items-center justify-center hover:bg-slate-900"
            >
              <MdClose className="text-xl" />
            </button>
            <img
              src={previewImageModal}
              alt="Screenshot Preview"
              className="w-full h-auto max-h-[80vh] object-contain rounded-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AppGuideManager;
